import { createElement } from "react";

/**
 * Icon node data: array of [elementName, attributes] tuples.
 * This is the internal representation used by lucide — we resolve it
 * from the icon name on the server and pass it as serializable data.
 */
export type IconNode = [string, Record<string, string>][];

interface IconProps {
    /** Pre-resolved icon node data */
    node: IconNode;
    /** Icon size in pixels (default: 24) */
    size?: number;
    /** Additional CSS class */
    className?: string;
    /** Stroke width (default: 2) */
    strokeWidth?: number;
}

/**
 * Renders pre-resolved lucide icon node data as an inline SVG.
 * No lucide-react dependency — works in both server and client components.
 */
export function Icon({ node, size = 24, className, strokeWidth = 2 }: IconProps) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            {node.map(([tag, attrs], i) => {
                // Remove the 'key' prop that lucide uses internally — React needs 'key' as a prop, not as an SVG attribute
                const { key: _key, ...svgAttrs } = attrs;
                return createElement(tag, { key: i, ...svgAttrs });
            })}
        </svg>
    );
}
